// AdminTotpReverifyModal — Re-verify TOTP L1 của admin cho các action
// đắt tiền / không thể hoàn tác (ví dụ: rebuild embedding toàn bộ).
//
// KHÁC L2OtpModal:
//   - L2OtpModal: NV (employee) nhập TOTP L2 để unlock phiên kiosk 4h.
//   - AdminTotpReverifyModal: chính admin đang login nhập TOTP L1 của mình
//     NGAY LÚC thao tác — không cache, không session.
//
// Props:
//   - open: bool
//   - title: string — tiêu đề modal (vd: "Xác nhận Rebuild embedding")
//   - description: string — mô tả action (vd: "Sẽ re-embed N câu · ~$X")
//   - onClose(): đóng không thực hiện
//   - onSubmit(code): parent gọi API với code — parent tự xử lý success/error
//     (parent THROW err.data?.code === 'ADMIN_TOTP_INVALID' để modal biết reset input)
//
// Parent pattern:
//   const [modalOpen, setModalOpen] = useState(false);
//   async function handleSubmit(code) {
//     const res = await api.someAction(body, { adminTotp: code });
//     toast.success(...); setModalOpen(false); reload();
//   }
//   <AdminTotpReverifyModal open={modalOpen} onClose={...} onSubmit={handleSubmit} />
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import TotpInput from './TotpInput.jsx';

export default function AdminTotpReverifyModal({
  open,
  title = '🔐 Xác minh lại TOTP admin',
  description = 'Nhập mã 6 số từ Google Authenticator của admin để tiếp tục.',
  onClose,
  onSubmit,
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [badCode, setBadCode] = useState(false);

  useEffect(() => {
    if (open) {
      setCode('');
      setBadCode(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, busy, onClose]);

  if (!open) return null;

  // codeOverride: truyền khi TotpInput auto-submit (tránh race với setCode).
  async function submit(e, codeOverride) {
    e?.preventDefault?.();
    const c = codeOverride ?? code;
    if (c.length !== 6 || busy) return;
    setBusy(true);
    setBadCode(false);
    try {
      await onSubmit(c);
      // parent đóng modal nếu thành công
    } catch (err) {
      const errCode = err?.data?.code;
      if (errCode === 'ADMIN_TOTP_INVALID') {
        setBadCode(true);
        setCode(''); // reset → TotpInput sẽ cho auto-submit lại khi user gõ 6 số khác
      } else if (errCode === 'ADMIN_TOTP_NOT_SETUP') {
        toast.error('Admin chưa setup TOTP L1 — vào /account/security để setup');
        onClose?.();
      } else {
        toast.error(err?.data?.error || err?.message || 'Thao tác thất bại');
        onClose?.();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4"
      >
        <div>
          <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
          <p className="text-sm text-slate-500 mt-1 whitespace-pre-wrap">{description}</p>
        </div>

        <TotpInput
          value={code}
          onChange={setCode}
          onSubmit={(c) => submit(null, c)}
          autoFocus
          disabled={busy}
          placeholder="000000"
          testId="admin-totp-reverify-input"
        />

        {badCode && (
          <div className="text-xs text-rose-700 bg-rose-50 rounded-lg px-3 py-2 border border-rose-200">
            Mã TOTP không đúng. Vui lòng kiểm tra lại trên app Authenticator.
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium disabled:opacity-50"
          >
            Huỷ
          </button>
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            className="flex-1 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50"
          >
            {busy ? 'Đang xử lý…' : 'Xác nhận'}
          </button>
        </div>
      </form>
    </div>
  );
}
